import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    ToggleButton,
    Divider,
    useTheme, useMediaQuery
} from '@mui/material';
import ElectricalServicesSharpIcon from '@mui/icons-material/ElectricalServicesSharp';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import DeleteIcon from '@mui/icons-material/Delete';
import WavesIcon from '@mui/icons-material/Waves';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InputField from '../../../components/InputField/InputField';
import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    createPersonalInfoSchema,
    type PersonalInfoInputs,
    isCellNumberRequired,
} from '../schemas/validationSchemas';
import { getIndigentTenantRules } from '../tenant/indigentTenantRules';
import { indigentValidationService } from '../../../services/indigentValidationService';
import { sanitizeMoneyInput } from '@/utils/moneyInput';
import { useResponsiveToast } from '../../../components/Toast';

// Utility function to format text with proper capitalization
const formatTextWithCapitalization = (text: string): string => {
    return text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

type StepProps = {
    formData: PersonalInfoInputs;
    setFormData: (newData: Partial<PersonalInfoInputs>) => void;
    onValidationChange: (isValid: boolean) => void;
    mode?: 'create' | 'edit';
    isEditable?: boolean;
    indigentNumber?: string; // Add indigent number prop
};

export interface StepPersonalInfoRef {
    validatePersonalIdCombination: () => Promise<boolean>;
    isValidating: boolean;
    runValidation: () => Promise<boolean>;
}

const StepPersonalInfo = forwardRef<StepPersonalInfoRef, StepProps>(({
    formData,
    setFormData,
    onValidationChange,
    mode = 'create',
    isEditable = true,
    indigentNumber,
}, ref) => {
    const currentYear = new Date().getFullYear();
    // Generate default financial years (current year + 5 years)
    const defaultFinancialYears = Array.from({ length: 6 }, (_, i) => currentYear + i);
    
    // For edit mode, include the existing financial year if it's not in the default range
    const existingFinancialYear = formData.financialYear ? parseInt(formData.financialYear, 10) : null;
    const financialYears = existingFinancialYear && !defaultFinancialYears.includes(existingFinancialYear)
        ? [existingFinancialYear, ...defaultFinancialYears].sort((a, b) => a - b)
        : defaultFinancialYears;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { error: showError } = useResponsiveToast();
    const [isValidating, setIsValidating] = useState(false);

    const tenantRules = useMemo(() => getIndigentTenantRules(), []);
    const personalInfoSchema = useMemo(
        () => createPersonalInfoSchema(tenantRules),
        [tenantRules],
    );
    const cellNumberRequired = isCellNumberRequired();

    const {
        control,
        watch,
        formState: { errors },
        setValue,
        trigger,
        getValues,
    } = useForm<PersonalInfoInputs>({
        resolver: zodResolver(personalInfoSchema),
        defaultValues: { ...formData, email: formData.email ?? '' },
        mode: 'all',
        criteriaMode: 'all',
    });

    // Simple validation function to check if all required fields are present
    const validateRequiredFields = useCallback(
        (data: any) => {
            const emailOk =
                !tenantRules.emailRequired ||
                (typeof data.email === 'string' && data.email.trim().length > 0);
            return !!(
                data.financialYear &&
                data.name &&
                data.surname &&
                data.personalId &&
                (!cellNumberRequired || data.cellNumber) &&
                emailOk &&
                data.services &&
                data.services.length > 0
            );
        },
        [cellNumberRequired, tenantRules.emailRequired],
    );

    // Function to validate Personal ID and Financial Year combination
    const validatePersonalIdCombination = useCallback(async (personalId: string, financialYear: string): Promise<boolean> => {
        try {
            setIsValidating(true);
            
            const response = await indigentValidationService.validatePersonalId(personalId, financialYear, indigentNumber);
            
            if (!response.valid) {
                showError('A record with this Personal ID and Financial Year combination already exists.');
                return false;
            }
            
            return true;
        } catch (error) {
            console.error('Validation error:', error);
            showError('Error validating Personal ID. Please try again.');
            return false;
        } finally {
            setIsValidating(false);
        }
    }, [indigentNumber, showError]);

    // Expose validation function to parent component
    useImperativeHandle(ref, () => ({
        validatePersonalIdCombination: () => {
            const currentValues = getValues();
            if (currentValues.personalId && currentValues.financialYear) {
                return validatePersonalIdCombination(currentValues.personalId, currentValues.financialYear);
            }
            return Promise.resolve(true);
        },
        isValidating,
        runValidation: () => {
            return trigger();
        },
    }), [getValues, isValidating, validatePersonalIdCombination, trigger]);

    // Initialize form validation
    useEffect(() => {
        // Set initial values and trigger validation
        Object.entries(formData).forEach(([key, value]) => {
            if (value) {
                setValue(key as keyof PersonalInfoInputs, value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
        });
        trigger();
    }, [formData, setValue, trigger]); // Added missing dependencies

    // Watch for form changes
    useEffect(() => {
        const subscription = watch(value => {
            if (value) {
                setFormData(value as PersonalInfoInputs);
                trigger().then(isValid => {
                    // In edit mode, if we have all required fields, consider it valid
                    if (mode === 'edit' && validateRequiredFields(value)) {
                        onValidationChange(true);
                    } else {
                        onValidationChange(isValid);
                    }
                });
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setFormData, trigger, onValidationChange, errors, mode, validateRequiredFields]);

    const handleToggleService = (value: string) => {
        const currentServices = getValues('services') || [];
        const exists = currentServices.includes(value);
        const updated = exists
            ? currentServices.filter((service: string) => service !== value)
            : [...currentServices, value];
        setValue('services', updated, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Financial Year */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Controller<PersonalInfoInputs>
                    name='financialYear'
                    control={control}
                    render={({
                        field: { onChange, value, ...field },
                        fieldState: { error, isTouched },
                    }) => (
                        <FormControl
                            variant='filled'
                            sx={{
                                backgroundColor: '#FEFBFF',
                                minWidth: isMobile ? '100%' : '45%',
                                width: isMobile ? '100%' : 'auto',
                                '& .MuiFilledInput-root': {
                                    backgroundColor: '#FEFBFF',
                                },
                                // Override disabled styles to maintain normal appearance
                                '& .MuiFilledInput-root.Mui-disabled': {
                                    backgroundColor: '#FEFBFF',
                                    '& fieldset': {
                                        borderColor: '#E0E0E0',
                                    },
                                    '& input': {
                                        color: '#000000',
                                        WebkitTextFillColor: '#000000',
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-disabled': {
                                    color: '#6B7280',
                                },
                            }}
                            error={isTouched && !!error}
                        >
                            <InputLabel
                                sx={{
                                    color: '#6B7280',
                                    fontSize: 13,
                                    '&.Mui-focused': {
                                        color: '#2D4677',
                                    },
                                }}
                                required
                            >
                                Financial Year
                            </InputLabel>
                            <Select value={value || ''} onChange={onChange} {...field} disabled={!isEditable}>
                                {financialYears.map(year => (
                                    <MenuItem key={year} value={year.toString()}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />
            </Box>

            <Divider sx={{ my: 2, borderColor: '#F1F1F1' }} />

            {/* Name & Surname */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Controller<PersonalInfoInputs>
                    name='name'
                    control={control}
                    render={({ field, fieldState: { error, isTouched } }) => (
                        <InputField
                            label='First Name'
                            required
                            fullWidth
                            error={isTouched && !!error}
                            helperText={isTouched && error?.message}
                            {...field}
                            onChange={(e) => {
                                const formattedValue = formatTextWithCapitalization(e.target.value);
                                field.onChange(formattedValue);
                            }}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                <Controller<PersonalInfoInputs>
                    name='surname'
                    control={control}
                    render={({ field, fieldState: { error, isTouched } }) => (
                        <InputField
                            label='Last Name'
                            required
                            fullWidth
                            error={isTouched && !!error}
                            helperText={isTouched && error?.message}
                            {...field}
                            onChange={(e) => {
                                const formattedValue = formatTextWithCapitalization(e.target.value);
                                field.onChange(formattedValue);
                            }}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
            </Box>

            {/* Personal ID & Other Fields */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 4,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Controller<PersonalInfoInputs>
                    name='personalId'
                    control={control}
                    render={({ field, fieldState: { error, isTouched } }) => (
                        <InputField
                            label='Personal ID'
                            required
                            placeholder='e.g., 8801015009087'
                            fullWidth
                            error={isTouched && !!error}
                            helperText={isTouched && error?.message}
                            {...field}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                <Controller<PersonalInfoInputs>
                    name='employerName'
                    control={control}
                    render={({ field }) => (
                        <InputField
                            label='Employer Name'
                            fullWidth
                            {...field}
                            onChange={(e) => {
                                const formattedValue = formatTextWithCapitalization(e.target.value);
                                field.onChange(formattedValue);
                            }}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                <Controller<PersonalInfoInputs>
                    name='income'
                    control={control}
                    render={({ field }) => (
                        <InputField
                            label='Monthly Income R'
                            fullWidth
                            required
                            name={field.name}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            value={field.value !== undefined && field.value !== null && field.value !== '' ? `R${field.value}` : ''}
                            onChange={e => {
                                const withoutPrefix = e.target.value.replace(/^R\s*/i, '');
                                field.onChange(sanitizeMoneyInput(withoutPrefix));
                            }}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                <Controller<PersonalInfoInputs>
                    name='incomeSource'
                    control={control}
                    render={({ field, fieldState: { error, isTouched } }) => (
                        <InputField
                            label='Source of Income'
                            placeholder='e.g., Salary or Pension'
                            fullWidth
                            error={isTouched && !!error}
                            helperText={isTouched && error?.message}
                            {...field}
                            onChange={(e) => {
                                const formattedValue = formatTextWithCapitalization(e.target.value);
                                field.onChange(formattedValue);
                            }}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                <Controller<PersonalInfoInputs>
                    name='cellNumber'
                    control={control}
                    render={({ field, fieldState: { error, isTouched } }) => (
                        <InputField
                            label='Cell Number'
                            required={cellNumberRequired}
                            placeholder='0821234567'
                            fullWidth
                            error={isTouched && !!error}
                            helperText={isTouched && error?.message}
                            {...field}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                {(tenantRules.emailVisible || Boolean((formData.email ?? '').trim())) && (
                    <Controller<PersonalInfoInputs>
                        name='email'
                        control={control}
                        render={({ field, fieldState: { error, isTouched } }) => (
                            <InputField
                                label='Email Address'
                                required={
                                    tenantRules.emailRequired && tenantRules.emailVisible
                                }
                                placeholder='name@example.com'
                                type='email'
                                fullWidth
                                error={isTouched && !!error}
                                helperText={isTouched && error?.message}
                                {...field}
                                disabled={!isEditable}
                                sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                            />
                        )}
                    />
                )}
            </Box>

            <Divider sx={{ my: 2, borderColor: '#F1F1F1' }} />

            {/* Services */}
            <Box mt={2}>
                <Box display='flex' justifyContent={isMobile ? 'flex-start' : 'space-between'} flexWrap='wrap' gap={2}>
                    {[
                        {
                            label: 'Electricity',
                            value: 'electricity',
                            icon: <ElectricalServicesSharpIcon />,
                        },
                        { label: 'Water', value: 'water', icon: <WaterDropIcon /> },
                        { label: 'Waste', value: 'waste', icon: <DeleteIcon /> },
                        { label: 'Waste Water', value: 'wastewater', icon: <WavesIcon /> },
                    ].map(({ label, value, icon }) => {
                        const isSelected = watch().services?.includes(value);
                        return (
                            <ToggleButton
                                key={value}
                                value={value}
                                selected={isSelected}
                                onChange={() => handleToggleService(value)}
                                disabled={!isEditable}
                                sx={{
                                    cursor: isEditable ? 'pointer' : 'default',
                                    width: isMobile ? 'calc(50% - 8px)' : '200px',
                                    height: isMobile ? '72px' : '86px',
                                    border: '2.5px solid',
                                    borderRadius: '14px',
                                    padding: '16px 12px',
                                    justifyContent: 'space-between',
                                    textTransform: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#FEFBFF',
                                    borderColor: isSelected ? '#2D4677' : '#E0E0E0',
                                    color: isSelected ? '#2D4677' : '#9E9E9E',
                                    '&:hover': {
                                        backgroundColor: '#FEFBFF',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#FEFBFF !important',
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#FEFBFF !important',
                                    },
                                    // Override disabled styles to maintain normal appearance
                                    '&.Mui-disabled': {
                                        backgroundColor: '#FEFBFF',
                                        borderColor: isSelected ? '#2D4677' : '#E0E0E0',
                                        color: isSelected ? '#2D4677' : '#9E9E9E',
                                        opacity: 1,
                                    },
                                }}
                            >
                                <Box display='flex' alignItems='center' gap={1}>
                                    {React.cloneElement(icon, {
                                        fontSize: 'small',
                                        sx: { color: isSelected ? '#0A1D56' : '#B0B0B0' },
                                    })}
                                    <Typography fontSize='14px' fontWeight={500} color='black'>
                                        {label}
                                    </Typography>
                                </Box>

                                {isSelected ? (
                                    <CheckBoxIcon sx={{ fontSize: 20, color: '#0A1D56' }} />
                                ) : (
                                    <CheckBoxOutlineBlankIcon
                                        sx={{ fontSize: 20, color: '#C5C5C5' }}
                                    />
                                )}
                            </ToggleButton>
                        );
                    })}
                </Box>
                {errors.services && (
                    <Typography color='error' sx={{ mt: 1, fontSize: 12 }}>
                        {errors.services.message}
                    </Typography>
                )}
            </Box>
        </Box>
    );
});

StepPersonalInfo.displayName = 'StepPersonalInfo';

export default StepPersonalInfo;
